import { useState, useCallback, useMemo } from 'react';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qDiagnosesByEncounter,
  qDiagnosisSuggestions,
  type PatientDiagnosisEntryRow,
  type VttSuggestionRow,
} from '@helios/data-access';
import { ChipListField, type Chip } from './ChipListField';

/* ------------------------------------------------------------------ */
/*  Diagnosis structured entry (PRD.214 Phase 2)                      */
/* ------------------------------------------------------------------ */

interface Props {
  patientId: string;
  encounterId: string;
  /** When true, hide the internal "Diagnosis" header (used inside guided-note cards). */
  hideHeader?: boolean;
}

export function EncounterDiagnosesTab({ patientId, encounterId, hideHeader }: Props) {
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';

  const { data: entries = [] } = usePowerSyncQuery<PatientDiagnosisEntryRow>(
    qDiagnosesByEncounter(encounterId),
  );

  const [search, setSearch] = useState('');
  const { data: suggestionRows = [] } = usePowerSyncQuery<VttSuggestionRow>(
    qDiagnosisSuggestions(orgId, search),
  );
  const suggestions = useMemo(() => suggestionRows.map((r) => r.label), [suggestionRows]);

  const chips: Chip[] = useMemo(
    () => entries.map((e) => ({ id: e.id, label: e.label })),
    [entries],
  );

  const handleAdd = useCallback(
    async (label: string) => {
      if (!db) return;
      const now = new Date().toISOString();
      await db.execute(
        `INSERT INTO patient_diagnosis_entries
          (id, org_id, clinic_id, patient_id, encounter_id, label, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
        [crypto.randomUUID(), orgId, null, patientId, encounterId, label, now, now],
      );
    },
    [db, orgId, patientId, encounterId],
  );

  const handleRemove = useCallback(
    async (chip: Chip) => {
      if (!db) return;
      await db.execute(`DELETE FROM patient_diagnosis_entries WHERE id = ?`, [chip.id]);
    },
    [db],
  );

  return (
    <div
      style={{
        padding: 'var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-3)',
      }}
    >
      {!hideHeader && (
        <div>
          <h2
            style={{
              margin: 0,
              fontSize: 'var(--font-size-lg)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            Diagnosis
          </h2>
          <p
            style={{
              margin: '4px 0 0',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
            }}
          >
            Working diagnosis for this encounter.
          </p>
        </div>
      )}
      <ChipListField
        chips={chips}
        suggestions={suggestions}
        onSearchChange={setSearch}
        onAdd={handleAdd}
        onRemove={handleRemove}
        subtitle="Add or edit the diagnosis"
        addLabel="Add Diagnosis"
        placeholder="Add or edit diagnosis…"
      />
    </div>
  );
}
